/* UV Main Collection | Horizon 4.1.5 compatible | Standalone UV asset */
const MAX_AGE = 6 * 60 * 60 * 1000;

class UVMainCollection extends HTMLElement {
  #observer;
  #applyScheduled = false;
  #loadingTimer;
  #paginationLoadingTimer;

  connectedCallback() {
    this.addEventListener('click', this.#handlePaginationClick, true);
    this.addEventListener('click', this.#handleProductClick, true);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('popstate', this.#scheduleApply);

    this.#observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        if (!(mutation.target instanceof Element)) return false;
        return (
          mutation.target.matches('.product-grid, .collection-wrapper, results-list') ||
          mutation.target.closest('.product-grid, .collection-wrapper, results-list') ||
          [...mutation.addedNodes].some(
            (node) =>
              node instanceof Element &&
              (node.matches?.('.product-grid, .product-grid__item, results-list') ||
                node.querySelector?.('.product-grid, .product-grid__item'))
          )
        );
      });

      if (relevant) {
        this.#clearPaginationLoading();
        this.#scheduleApply();
      }
    });

    this.#observer.observe(this, { childList: true, subtree: true });
    this.#scheduleApply();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handlePaginationClick, true);
    this.removeEventListener('click', this.#handleProductClick, true);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('popstate', this.#scheduleApply);
    this.#observer?.disconnect();
    clearTimeout(this.#loadingTimer);
    clearTimeout(this.#paginationLoadingTimer);
  }

  get #sectionId() {
    return this.dataset.sectionId || '';
  }

  get #config() {
    const node = this.querySelector('[data-uv-main-collection-config]');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  get #storageKey() {
    const locale = window.Shopify?.locale || document.documentElement.lang || 'default';
    const handle = this.dataset.collectionHandle || this.#config.collectionHandle || '';
    return `uv_collection_pin_v3:${locale}:${handle}`;
  }

  #filterSignature(url = new URL(window.location.href)) {
    const entries = [];

    for (const [key, value] of url.searchParams.entries()) {
      if (key === 'sort_by' || key.startsWith('filter.')) {
        entries.push([key, value]);
      }
    }

    entries.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
    return new URLSearchParams(entries).toString();
  }

  #readState() {
    try {
      const state = JSON.parse(sessionStorage.getItem(this.#storageKey) || 'null');
      if (!state?.handle || !state?.createdAt) return null;

      if (Date.now() - Number(state.createdAt) > MAX_AGE) {
        sessionStorage.removeItem(this.#storageKey);
        return null;
      }

      return state;
    } catch (_) {
      return null;
    }
  }

  #writeState(state) {
    try {
      sessionStorage.setItem(this.#storageKey, JSON.stringify(state));
    } catch (_) {}
  }

  #currentPage() {
    const url = new URL(window.location.href);
    return Math.max(1, Number(url.searchParams.get('page') || 1) || 1);
  }

  #isStateActive(state) {
    if (!state) return false;

    return (
      Number(state.pinnedPage || 1) === this.#currentPage() &&
      state.signature === this.#filterSignature()
    );
  }

  #scheduleApply = () => {
    if (this.#applyScheduled) return;
    this.#applyScheduled = true;

    requestAnimationFrame(() => {
      this.#applyScheduled = false;
      this.#enhancePagination();
      this.#applyPin();
    });
  };

  #revealGrid(grid = null) {
    // Backward-compat cleanup plus the pre-rendered slot/loading state.
    if (this.#sectionId) {
      document.documentElement.classList.remove(
        `uv-collection-pin-pending-${this.#sectionId}`,
        `uv-collection-first-slot-${this.#sectionId}`,
        `uv-collection-first-loading-${this.#sectionId}`
      );
    }

    const grids = grid
      ? [grid]
      : [...this.querySelectorAll('ul.product-grid[ref="grid"]')];

    for (const currentGrid of grids) {
      currentGrid.classList.remove('is-first-product-loading');
      currentGrid
        .querySelector(':scope > .uv-first-product-placeholder')
        ?.remove();
    }
  }

  #hasUsableSnapshot(state) {
    if (!state?.product) return false;

    const currentCurrency = window.Shopify?.currency?.active || '';
    const currencyMatches =
      !state.currency || !currentCurrency || state.currency === currentCurrency;
    const variantMatches =
      String(state.product.requested_variant_id || '') === String(state.variant || '');

    return currencyMatches && variantMatches;
  }

  #showFirstProductPlaceholder(grid) {
    if (!grid) return null;

    if (this.#sectionId) {
      document.documentElement.classList.add(
        `uv-collection-first-slot-${this.#sectionId}`,
        `uv-collection-first-loading-${this.#sectionId}`
      );
    }

    const existing = grid.querySelector(':scope > .uv-first-product-placeholder');
    if (existing) {
      existing.setAttribute('aria-busy', 'true');
      grid.classList.add('is-first-product-loading');
      return existing;
    }

    // Defensive fallback for a grid replaced by third-party code without the
    // server-rendered placeholder. Normal Horizon renders never need this path.
    const config = this.#config;
    const item = document.createElement('li');
    item.className = 'uv-first-product-placeholder';
    item.dataset.page = String(this.#currentPage());
    item.setAttribute('aria-busy', 'true');
    item.setAttribute('aria-live', 'polite');

    const card = document.createElement('article');
    card.className = 'uv-collection-card uv-collection-card--placeholder';

    const media = document.createElement('div');
    media.className = 'uv-collection-card__media uv-collection-card__media--placeholder';

    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.textContent = 'Loading featured product';
    media.appendChild(status);

    const info = document.createElement('div');
    info.className = 'uv-collection-card__info uv-collection-card__info--placeholder';
    info.setAttribute('aria-hidden', 'true');

    if (config.showTitle) {
      const titleSpacer = document.createElement('span');
      titleSpacer.className = 'uv-collection-card__title';
      titleSpacer.textContent = '\u00a0';
      info.appendChild(titleSpacer);
    }

    if (config.showPrice !== false) {
      const priceSpacer = document.createElement('div');
      priceSpacer.className = 'uv-collection-card__price';
      priceSpacer.textContent = '\u00a0';
      info.appendChild(priceSpacer);
    }

    card.append(media, info);
    item.appendChild(card);

    grid.classList.add('is-first-product-loading');
    grid.insertBefore(item, grid.firstElementChild || null);
    return item;
  }

  #enhancePagination() {
    const nav = this.querySelector('nav.pagination');
    if (!nav || nav.querySelector(':scope > .uv-pagination-step')) return;

    const list = nav.querySelector(':scope > .pagination__list');
    if (!list) return;

    const arrowControls = list.querySelectorAll('.pagination__link--arrow');
    if (arrowControls.length < 2) return;

    const previousControl = arrowControls[0];
    const nextControl = arrowControls[arrowControls.length - 1];
    const config = this.#config;

    const row = document.createElement('div');
    row.className = 'uv-pagination-step';
    row.setAttribute('aria-label', nav.getAttribute('aria-label') || 'Pagination');

    const buildButton = (control, direction) => {
      const isDisabled =
        control.getAttribute('aria-disabled') === 'true' ||
        control.classList.contains('pagination__link--disabled') ||
        !(control instanceof HTMLAnchorElement);

      const element = isDisabled ? document.createElement('span') : document.createElement('a');
      element.className = `uv-pagination-step__button uv-pagination-step__button--${direction}`;

      const label =
        direction === 'previous'
          ? config.previousLabel || 'Previous'
          : config.nextLabel || 'Next';

      element.textContent = direction === 'previous' ? `← ${label}` : `${label} →`;
      element.setAttribute('aria-label', label);

      if (isDisabled) {
        element.setAttribute('aria-disabled', 'true');
      } else {
        element.href = control.href;
      }

      return element;
    };

    row.append(
      buildButton(previousControl, 'previous'),
      buildButton(nextControl, 'next')
    );

    nav.insertBefore(row, list);
  }

  #findCard(grid, handle) {
    if (!grid || !handle) return null;

    const wanted = String(handle).toLowerCase();
    for (const card of grid.querySelectorAll(':scope > li.product-grid__item[data-product-handle]')) {
      if (String(card.dataset.productHandle || '').toLowerCase() === wanted) return card;
    }

    return null;
  }

  #makeImageImmediate(card) {
    const image = card?.querySelector('img.uv-collection-card__image');
    if (!image) return;

    image.loading = 'eager';
    image.fetchPriority = 'high';
    image.decoding = 'async';
  }

  #pinCard(grid, card, state) {
    if (!grid || !card) return false;

    card.dataset.page = String(state.pinnedPage || 1);
    card.dataset.uvPinned = 'true';

    const placeholder = grid.querySelector(':scope > .uv-first-product-placeholder');
    const first = grid.querySelector(':scope > li.product-grid__item');

    if (placeholder && placeholder !== card) {
      // If the real card already exists later in this grid, replaceWith() moves it
      // into slot #1 and removes the placeholder in one DOM operation.
      placeholder.replaceWith(card);
    } else if (first !== card) {
      grid.insertBefore(card, first || null);
    }

    this.#makeImageImmediate(card);
    this.#removeSamePageDuplicate(grid, card.dataset.productId, card);
    this.#revealGrid(grid);
    return true;
  }

  #removeSamePageDuplicate(grid, productId, pinnedCard) {
    if (!productId) return;

    for (const candidate of grid.querySelectorAll(`:scope > li.product-grid__item[data-product-id="${CSS.escape(productId)}"]`)) {
      if (candidate !== pinnedCard && candidate.dataset.page === pinnedCard.dataset.page) {
        candidate.remove();
      }
    }
  }

  #withWidth(src, width) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  #imageSource(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || image.preview_image?.src || image.preview_image?.url || '';
  }

  #snapshotProduct(full, state) {
    const requestedVariantId = String(state.variant || '');
    const selectedVariant = requestedVariantId
      ? full.variants?.find((variant) => String(variant.id) === requestedVariantId)
      : null;

    const selectedVariantImageObject =
      selectedVariant?.featured_image ||
      selectedVariant?.featured_media?.preview_image ||
      null;
    const selectedVariantImage = this.#imageSource(selectedVariantImageObject);
    const fallbackImageObject = full.featured_image || full.images?.[0] || null;
    const fallbackImage = this.#imageSource(fallbackImageObject);
    const imageObject = selectedVariantImageObject || fallbackImageObject;

    return {
      id: full.id,
      handle: full.handle || state.handle,
      title: full.title || '',
      featured_image: selectedVariantImage || fallbackImage,
      image_alt:
        imageObject && typeof imageObject === 'object'
          ? imageObject.alt || full.title || ''
          : full.title || '',
      image_width: Number(
        imageObject && typeof imageObject === 'object' ? imageObject.width || 0 : 0
      ),
      image_height: Number(
        imageObject && typeof imageObject === 'object' ? imageObject.height || 0 : 0
      ),
      price: Number(selectedVariant?.price ?? full.price ?? 0),
      compare_at_price: Number(selectedVariant?.compare_at_price ?? full.compare_at_price ?? 0),
      available: selectedVariant ? selectedVariant.available !== false : full.available !== false,
      requested_variant_id: requestedVariantId,
      variant_id: selectedVariant ? String(selectedVariant.id) : '',
      variant_title: selectedVariant?.title || '',
      variant_image: selectedVariantImage,
    };
  }

  #updateCardForProduct(card, product, state) {
    if (!card || !product) return;

    const config = this.#config;
    const href =
      state.href ||
      `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle || state.handle)}`;

    card.dataset.productId = String(product.id || card.dataset.productId || '');
    card.dataset.productHandle = product.handle || state.handle;
    if (state.variant) card.dataset.uvVariantId = String(state.variant);
    else delete card.dataset.uvVariantId;

    for (const link of card.querySelectorAll(
      '.uv-collection-card__media-link, .uv-collection-card__title'
    )) {
      if (link instanceof HTMLAnchorElement) link.href = href;
    }

    const featuredImage = product.featured_image || '';
    const image = card.querySelector('img.uv-collection-card__image');
    if (image && featuredImage) {
      const widths = [165, 360, 533, 720, 900, 1200, 1500];
      image.srcset = widths
        .map((width) => `${this.#withWidth(featuredImage, width)} ${width}w`)
        .join(', ');
      image.src = this.#withWidth(featuredImage, 800);
      image.sizes = this.#responsiveSizes();
      image.alt = product.image_alt || product.title || state.handle;
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.decoding = 'async';

      if (Number(product.image_width) > 0) image.width = Number(product.image_width);
      if (Number(product.image_height) > 0) image.height = Number(product.image_height);
    }

    const price = Number(product.price || 0);
    const compareAt = Number(product.compare_at_price || 0);
    const onSale = compareAt > price;

    const priceBox = card.querySelector('.uv-collection-card__price');
    if (priceBox) {
      priceBox.classList.toggle('uv-collection-card__price--sale', onSale);
      priceBox.setAttribute(
        'aria-label',
        `Price: ${this.#formatMoney(price)}${onSale ? `, was ${this.#formatMoney(compareAt)}` : ''}`
      );
      priceBox.replaceChildren(document.createTextNode(this.#formatMoney(price)));

      if (onSale) {
        const compare = document.createElement('span');
        compare.className = 'uv-collection-card__compare';
        compare.setAttribute('aria-hidden', 'true');
        compare.textContent = this.#formatMoney(compareAt);
        priceBox.appendChild(compare);
      }
    }

    const media = card.querySelector('.uv-collection-card__media');
    const existingBadge = media?.querySelector('.uv-collection-card__badge');
    existingBadge?.remove();

    if (
      media &&
      onSale &&
      config.showSaleBadge &&
      product.available !== false
    ) {
      const discount = Math.round(((compareAt - price) * 100) / compareAt);
      const badge = document.createElement('span');
      badge.className = 'uv-collection-card__badge';
      badge.setAttribute('aria-label', `${discount}% off`);
      badge.textContent = `${config.showDiscountPercent ? `${discount}% ` : ''}${config.saleBadgeText || ''}`;
      media.appendChild(badge);
    }
  }

  #formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (_) {
      return `$${value.toFixed(2)}`;
    }
  }

  #responsiveSizes() {
    const config = this.#config;
    const desktop = Math.max(1, Number(config.columnsDesktop || 4));
    const mobile = Math.max(1, Number(config.columnsMobile || 2));
    return `(min-width: 750px) ${Math.round(10000 / desktop) / 100}vw, ${Math.round(10000 / mobile) / 100}vw`;
  }

  #buildQuickCard(product, state) {
    if (!product?.id) return null;

    const config = this.#config;
    const href = state.href || `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle || state.handle)}`;

    const item = document.createElement('li');
    item.id = `${this.#sectionId}-${product.id}`;
    item.className = 'product-grid__item product-grid__item--0';
    item.dataset.page = String(state.pinnedPage || 1);
    item.dataset.productId = String(product.id);
    item.dataset.productHandle = product.handle || state.handle;
    item.dataset.uvPinned = 'true';
    item.dataset.uvQuickCard = 'true';
    if (state.variant) item.dataset.uvVariantId = String(state.variant);
    item.setAttribute('ref', 'cards[]');

    const card = document.createElement('article');
    card.className = 'uv-collection-card';

    const media = document.createElement('div');
    media.className = 'uv-collection-card__media';

    const overlay = document.createElement('a');
    overlay.href = href;
    overlay.className = 'uv-collection-card__media-link';
    overlay.tabIndex = -1;
    overlay.setAttribute('aria-hidden', 'true');
    media.appendChild(overlay);

    const featuredImage = product.featured_image || '';
    if (featuredImage) {
      const image = document.createElement('img');
      const widths = [165, 360, 533, 720, 900, 1200, 1500];

      image.srcset = widths.map((width) => `${this.#withWidth(featuredImage, width)} ${width}w`).join(', ');
      image.src = this.#withWidth(featuredImage, 800);
      image.sizes = this.#responsiveSizes();
      image.alt = product.image_alt || product.title || state.handle;
      image.className = 'uv-collection-card__image';
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.decoding = 'async';
      image.width = Number(product.image_width) > 0 ? Number(product.image_width) : 1200;
      image.height =
        Number(product.image_height) > 0
          ? Number(product.image_height)
          : Math.max(1, Math.round(1200 * (Number(config.imageRatio || 110) / 100)));
      media.appendChild(image);
    }

    const price = Number(product.price || 0);
    const compareAt = Number(product.compare_at_price || 0);
    const onSale = compareAt > price;

    if (onSale && config.showSaleBadge && product.available !== false) {
      const discount = Math.round(((compareAt - price) * 100) / compareAt);
      const badge = document.createElement('span');
      badge.className = 'uv-collection-card__badge';
      badge.setAttribute('aria-label', `${discount}% off`);
      badge.textContent = `${config.showDiscountPercent ? `${discount}% ` : ''}${config.saleBadgeText || ''}`;
      media.appendChild(badge);
    }

    card.appendChild(media);

    const info = document.createElement('div');
    info.className = 'uv-collection-card__info';

    if (config.showTitle) {
      const title = document.createElement('a');
      title.href = href;
      title.className = 'uv-collection-card__title';
      title.textContent = product.title || state.handle;
      info.appendChild(title);
    }

    if (config.showPrice !== false) {
      const priceBox = document.createElement('div');
      priceBox.className = `uv-collection-card__price${onSale ? ' uv-collection-card__price--sale' : ''}`;
      priceBox.setAttribute(
        'aria-label',
        `Price: ${this.#formatMoney(price)}${onSale ? `, was ${this.#formatMoney(compareAt)}` : ''}`
      );
      priceBox.appendChild(document.createTextNode(this.#formatMoney(price)));

      if (onSale) {
        const compare = document.createElement('span');
        compare.className = 'uv-collection-card__compare';
        compare.setAttribute('aria-hidden', 'true');
        compare.textContent = this.#formatMoney(compareAt);
        priceBox.appendChild(compare);
      }

      info.appendChild(priceBox);
    }
    card.appendChild(info);
    item.appendChild(card);

    return item;
  }

  async #getProduct(state) {
    const currentCurrency = window.Shopify?.currency?.active || '';
    const snapshotCurrencyMatches =
      !state.currency || !currentCurrency || state.currency === currentCurrency;
    const snapshotVariantMatches =
      state.product &&
      String(state.product.requested_variant_id || '') === String(state.variant || '');

    if (state.product && snapshotCurrencyMatches && snapshotVariantMatches) {
      return state.product;
    }

    const boot = window.__uvCollectionPins?.[this.#sectionId];
    const bootMatches =
      boot?.productPromise &&
      boot.state?.handle?.toLowerCase() === state.handle.toLowerCase() &&
      String(boot.state?.variant || '') === String(state.variant || '');

    if (bootMatches) {
      const product = await boot.productPromise;
      if (product) return product;
    }

    const endpoint = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(state.handle)}.js`;

    try {
      const response = await fetch(endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 404) sessionStorage.removeItem(this.#storageKey);
        return null;
      }

      const full = await response.json();
      const product = this.#snapshotProduct(full, state);

      state.product = product;
      state.currency = currentCurrency;
      this.#writeState(state);
      return product;
    } catch (error) {
      console.warn('[UV Main Collection] First-product request failed.', error);
      return null;
    }
  }

  async #applyPin() {
    const grid = this.querySelector('ul.product-grid[ref="grid"]');
    if (!grid) {
      this.#revealGrid();
      return;
    }

    const state = this.#readState();
    if (!this.#isStateActive(state)) {
      this.#revealGrid(grid);
      return;
    }

    const existing = this.#findCard(grid, state.handle);

    // Fast path: without a requested variant, the native server-rendered card
    // can be moved immediately with no product Ajax request or loading flash.
    if (existing && !state.variant) {
      this.#pinCard(grid, existing, state);
      return;
    }

    const alreadyPinned = grid.querySelector(':scope > li[data-uv-pinned="true"]');
    if (
      alreadyPinned?.dataset.productHandle?.toLowerCase() === state.handle.toLowerCase() &&
      !state.variant
    ) {
      this.#pinCard(grid, alreadyPinned, state);
      return;
    }

    // Only reserve slot #1 when product data genuinely has to resolve. Cached
    // snapshots and native-card fast paths remain spinner-free.
    if (!this.#hasUsableSnapshot(state)) {
      this.#showFirstProductPlaceholder(grid);
    }

    // Variant-aware / off-page path: resolve the exact product before filling slot #1.
    const product = await this.#getProduct(state);
    if (!product || !this.isConnected) {
      this.#revealGrid();
      return;
    }

    // Filters/sorting can replace the grid while the request is in flight. Never
    // commit an old response into a newer collection state.
    const latestState = this.#readState();
    const sameTarget =
      latestState &&
      String(latestState.handle || '').toLowerCase() === String(state.handle || '').toLowerCase() &&
      String(latestState.variant || '') === String(state.variant || '') &&
      latestState.signature === state.signature &&
      Number(latestState.pinnedPage || 1) === Number(state.pinnedPage || 1);

    if (!sameTarget || !this.#isStateActive(latestState)) {
      this.#revealGrid();
      return;
    }

    const latestGrid = this.querySelector('ul.product-grid[ref="grid"]');
    if (!latestGrid) {
      this.#revealGrid();
      return;
    }

    const serverCard = this.#findCard(latestGrid, latestState.handle);
    if (serverCard) {
      this.#updateCardForProduct(serverCard, product, latestState);
      this.#pinCard(latestGrid, serverCard, latestState);
      return;
    }

    const pinnedCard = latestGrid.querySelector(':scope > li[data-uv-pinned="true"]');
    if (pinnedCard?.dataset.productHandle?.toLowerCase() === latestState.handle.toLowerCase()) {
      this.#updateCardForProduct(pinnedCard, product, latestState);
      this.#pinCard(latestGrid, pinnedCard, latestState);
      return;
    }

    const quickCard = this.#buildQuickCard(product, latestState);
    if (quickCard) this.#pinCard(latestGrid, quickCard, latestState);
    else this.#revealGrid(latestGrid);
  }

  #clearPaginationLoading = () => {
    clearTimeout(this.#paginationLoadingTimer);

    for (const nav of this.querySelectorAll('nav.pagination.is-pagination-busy')) {
      nav.classList.remove('is-pagination-busy');
      nav.removeAttribute('aria-busy');
    }

    for (const control of this.querySelectorAll('.is-pagination-loading')) {
      control.classList.remove('is-pagination-loading');
      control.removeAttribute('aria-busy');
      control.style.removeProperty('--uv-pagination-spinner-color');

      const originalLabel = control.dataset.uvOriginalAriaLabel;
      if (originalLabel !== undefined) {
        if (originalLabel) control.setAttribute('aria-label', originalLabel);
        else control.removeAttribute('aria-label');
        delete control.dataset.uvOriginalAriaLabel;
      }
    }
  };

  #startPaginationLoading(control) {
    const nav = control.closest('nav.pagination');
    if (!nav) return;

    this.#clearPaginationLoading();

    const originalLabel = control.getAttribute('aria-label') || '';
    control.dataset.uvOriginalAriaLabel = originalLabel;

    const visibleLabel =
      originalLabel ||
      control.textContent?.trim() ||
      'page';

    const computedColor = getComputedStyle(control).color;
    if (computedColor) {
      control.style.setProperty('--uv-pagination-spinner-color', computedColor);
    }

    control.classList.add('is-pagination-loading');
    control.setAttribute('aria-busy', 'true');
    control.setAttribute('aria-label', `Loading ${visibleLabel}`);

    nav.classList.add('is-pagination-busy');
    nav.setAttribute('aria-busy', 'true');

    // Safety reset if navigation is blocked or fails.
    this.#paginationLoadingTimer = window.setTimeout(
      this.#clearPaginationLoading,
      8000
    );
  }

  #handlePaginationClick = (event) => {
    if (
      event.defaultPrevented ||
      !(event.target instanceof Element) ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const control = event.target.closest(
      'nav.pagination a.pagination__link, nav.pagination a.uv-pagination-step__button'
    );

    if (!control || control.closest('uv-main-collection') !== this) return;
    if (control.getAttribute('aria-disabled') === 'true') return;
    if ((control.getAttribute('target') || '').toLowerCase() === '_blank') return;

    // Ignore non-primary mouse clicks, but still support keyboard-activated clicks.
    if (event instanceof MouseEvent && event.button !== 0) return;

    this.#startPaginationLoading(control);
  };

  #clearLoading = () => {
    clearTimeout(this.#loadingTimer);

    for (const item of this.querySelectorAll('.product-grid__item.is-product-loading')) {
      item.classList.remove('is-product-loading');
      item.removeAttribute('aria-busy');
    }
  };

  #handlePageShow = () => {
    this.#clearLoading();
    this.#clearPaginationLoading();
    this.#scheduleApply();
  };

  #handleProductClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const link = event.target.closest('.uv-collection-card__media-link, .uv-collection-card__title');
    if (!link || link.closest('uv-main-collection') !== this) return;
    if ((link.getAttribute('target') || '').toLowerCase() === '_blank') return;

    const item = link.closest('.product-grid__item');
    if (!item) return;

    this.#clearLoading();
    item.classList.add('is-product-loading');
    item.setAttribute('aria-busy', 'true');

    this.#loadingTimer = window.setTimeout(this.#clearLoading, 4000);
  };
}

if (!customElements.get('uv-main-collection')) {
  customElements.define('uv-main-collection', UVMainCollection);
}
